import React, { useEffect, useState } from 'react';
import { CreditCard, Mail } from 'lucide-react';
// import { Crown } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  Action, Banner, ErrorNote, Field, Input, Panel, PanelHead, RouteMark, Tabs,
} from '../aa';
// import { aaiApi } from '../../services/aaiApi';
import {
  billingApi, type BillingAccount, type InvoicePage, type SeatSummary, type WalletSummary,
} from '../../services/billingApi';
// import type { RosterMember } from '../../types/aai';
import { InvoicesPanel, RECENT_INVOICES, UsageWalletPanel, finishCardSetup, loadWallet } from './UsageWalletPanel';
import { SeatPlanModal, finishSeatCheckout } from './SeatPlanModal';
import { RenewalPanel } from './RenewalPanel';
import { PROJECTS_BILLING_VIEW, ProjectBudgetsPanel } from './ProjectBudgetsPanel';
import { projectBudgetsApi, type ProjectBudget } from '../../services/projectBudgetsApi';
import { aaiApi } from '../../services/aaiApi';
import type { ActivityEvent } from '../../types/aai';
import { BILLING_HISTORY_LIMIT, BillingHistoryPanel } from './BillingHistoryPanel';
import { OwnCardPanel } from './OwnCardPanel';

// const NOT_TRANSFERABLE = new Set(['OWNER', 'CONSULTANT', 'EXPERT', 'TEAM_LEAD']);

export const BILLING_VIEW_KEY = 'aa_billing_view';

/* A project budget page sends the Owner back to the Project budgets tab. */
const readBillingView = (): string => {
  try {
    return sessionStorage.getItem(BILLING_VIEW_KEY) || 'billing';
  } catch {
    return 'billing';
  }
};

const clearBillingView = () => {
  try {
    sessionStorage.removeItem(BILLING_VIEW_KEY);
  } catch {
    /* nothing stored */
  }
};

const money = (cents: number) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

const renewalLabel = (iso: string | null | undefined, cancelling: boolean | undefined): string => {
  if (!iso) return '';
  const day = new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  if (cancelling) return `Your plan ends on ${day}.`;
  return `Renews ${day}`;
};

/* The plan the company pays for. Free is the Owner alone; the Team plan
 * pays for the seats the Owner picked, and grows when more people join. */
const PlanSection: React.FC<{
  summary: SeatSummary | null;
  onSummary: (summary: SeatSummary) => void;
  onRenewal: () => void;
}> = ({ summary, onSummary, onRenewal }) => {
  const [picking, setPicking] = useState(false);

  if (!summary) return <ErrorNote message="Couldn't load your plan. Refresh to try again." />;
  if (!summary.billed || !summary.can_manage) return null;

  const seats = summary.seats ?? 1;
  const joined = summary.joined ?? seats;
  const price = summary.seat_price_cents ?? 0;
  const people = `${joined} ${joined === 1 ? 'person' : 'people'}`;

  let title = 'Free';
  let desc = `Just you. To bring your team in, move to the Team plan: ${money(price)} per seat, per month, each with included agent usage.`;
  if (joined > 1) {
    desc = `${people} on the team. Free does not let you invite anyone else; the Team plan is ${money(price)} per seat, per month, each with included agent usage.`;
  }
  let action: React.ReactNode = <Action onClick={() => setPicking(true)}>Move to Team plan</Action>;
  const ended = summary.plan === 'free' ? summary.ended_plan : null;
  if (ended) {
    const when = ended.ended_at ? new Date(ended.ended_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    const had = ended.seats ? ` with ${ended.seats} ${ended.seats === 1 ? 'seat' : 'seats'}` : '';
    const why = ended.stopped_by_us ? ' Agents Anywhere stopped it.' : ' It was cancelled or not paid.';
    title = 'Team plan ended';
    desc = `Your Team plan${had} ended${when ? ` on ${when}` : ''}.${why} ${people} ${joined === 1 ? 'is' : 'are'} still on the team. Restart it to bring everyone back in.`;
    action = <Action onClick={() => setPicking(true)}>Restart Team plan</Action>;
  }
  if (summary.plan === 'workspace') {
    const open = Math.max(seats - joined, 0);
    title = `Team plan · ${money(summary.monthly_cents ?? seats * price)} a month`;
    desc = [
      `${seats} ${seats === 1 ? 'seat' : 'seats'} at ${money(price)} each: ${joined} in use, ${open} open.`,
      renewalLabel(summary.renews_at, summary.cancel_at_period_end),
      summary.renews_at && !summary.cancel_at_period_end ? 'It renews automatically and is charged to the card on your plan.' : '',
    ].filter(Boolean).join(' ');
    const renewal = <Action variant="outline" onClick={onRenewal}>Renewal</Action>;
    action = (
      <div className="flex flex-wrap gap-2">
        <Action variant="outline" onClick={() => setPicking(true)}>Add seats</Action>
        {renewal}
      </div>
    );
    if (summary.cancel_at_period_end) action = renewal;
  }
  if (summary.plan === 'legacy') {
    title = `${summary.plan_name} plan`;
    desc = `${people} on the team. At your next renewal this moves to per-seat billing: ${money(price)} per seat, per month.`;
    action = null;
  }

  return (
    <Panel>
      <PanelHead kicker="Plan" title={title} desc={desc} action={action} />
      <div className="px-6 pb-5 text-[12.5px] text-ink-dim">
        A person takes a seat once they accept their invite. If more people join than you have seats, a seat is added and charged for the rest of that month.
      </div>
      {picking && (
        <SeatPlanModal
          summary={summary}
          restartSeats={ended?.seats ?? undefined}
          onClose={() => setPicking(false)}
          onChanged={onSummary}
        />
      )}
    </Panel>
  );
};

// Transfer ownership is parked: once an Owner, always the Owner.
// /* Ownership and billing are one thing: the Owner pays. Handing the company to
//  * someone else moves the bill with it, so the action sits here. */
// const TransferOwnership: React.FC<{ clientId: string }> = ({ clientId }) => {
//   const [members, setMembers] = useState<RosterMember[] | null>(null);
//   const [target, setTarget] = useState('');
//   const [confirming, setConfirming] = useState(false);
//   const [busy, setBusy] = useState(false);
//
//   useEffect(() => {
//     aaiApi.getMembers()
//       .then(({ data }) => setMembers(data.members.filter((m) => !NOT_TRANSFERABLE.has((m.workspace_role || '').toUpperCase()))))
//       .catch(() => setMembers([]));
//   }, []);
//
//   const chosen = members?.find((m) => m.email === target);
//
//   const transfer = async () => {
//     if (!chosen) return;
//     setBusy(true);
//     try {
//       await billingApi.transferOwnership(clientId, chosen.email);
//       toast.success(`${chosen.name || chosen.email} is now the Owner.`);
//       window.location.assign('/dashboard');
//     } catch (err) {
//       const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
//       toast.error(detail || 'Could not transfer ownership. Try again.');
//       setBusy(false);
//     }
//   };
//
//   let body: React.ReactNode = <Loading label="Loading your team" />;
//   if (members && members.length === 0) {
//     body = <p className="m-0 text-[13px] text-ink-dim">Invite someone to the company first. Only a person who has joined can become the Owner.</p>;
//   }
//   if (members && members.length > 0) {
//     body = (
//       <div className="space-y-3">
//         <Field label="New Owner" htmlFor="new-owner">
//           <Select id="new-owner" value={target} onChange={(e) => { setTarget(e.target.value); setConfirming(false); }}>
//             <option value="">Pick a person</option>
//             {members.map((m) => <option key={m.email} value={m.email}>{m.name ? `${m.name} (${m.email})` : m.email}</option>)}
//           </Select>
//         </Field>
//         {!confirming && (
//           <div className="flex justify-end">
//             <Action variant="outline" onClick={() => setConfirming(true)} disabled={!chosen} reason="Pick a person first.">
//               Transfer ownership
//             </Action>
//           </div>
//         )}
//         {confirming && chosen && (
//           <div className="flex flex-wrap items-center justify-between gap-3 rounded-act border border-line px-4 py-3">
//             <span className="text-[13px] text-ink">
//               {chosen.name || chosen.email} becomes the Owner and pays for the company. You stay on as AI Champion.
//             </span>
//             <span className="flex gap-2">
//               <Action variant="quiet" onClick={() => setConfirming(false)} disabled={busy} reason="Working">Cancel</Action>
//               <Action variant="danger" onClick={transfer} disabled={busy} reason="Working">Yes, transfer</Action>
//             </span>
//           </div>
//         )}
//       </div>
//     );
//   }
//
//   return (
//     <Panel>
//       <PanelHead
//         kicker="Ownership"
//         title="Company Owner"
//         desc="A company has one Owner, and the Owner pays for it. Handing ownership over moves billing to the new Owner."
//       />
//       <div className="px-6 pb-5">
//         <div className="mb-4 flex items-center gap-2 text-[13px] text-ink">
//           <Crown size={16} className="text-ink-dim" /> You are the Owner.
//         </div>
//         {body}
//       </div>
//     </Panel>
//   );
// };

// clientId fed the parked Transfer ownership panel; kept so callers stay unchanged.
const Bar: React.FC<{ className: string }> = ({ className }) => (
  <span className={`block animate-pulse rounded bg-canvas-inset ${className}`} />
);

/* The page's own shape while it loads, so nothing moves when the figures
 * arrive: plan, usage with the amount tiles, billing email, invoices. */
const BillingSkeleton: React.FC = () => (
  <div aria-busy="true" aria-label="Loading billing" className="mx-auto max-w-[1000px] space-y-6">
    <Panel>
      <div className="flex items-start justify-between gap-6 px-6 py-6">
        <div className="flex-1 space-y-3"><Bar className="h-3 w-12" /><Bar className="h-5 w-56" /><Bar className="h-3 w-80 max-w-full" /></div>
        <Bar className="h-10 w-32 rounded-act" />
      </div>
      <div className="px-6 pb-6"><Bar className="h-3 w-full" /></div>
    </Panel>
    <Panel>
      <div className="space-y-3 px-6 py-6"><Bar className="h-3 w-14" /><Bar className="h-5 w-72 max-w-full" /><Bar className="h-3 w-full" /></div>
      <div className="space-y-4 px-6 pb-6">
        <div className="grid gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((i) => <Bar key={i} className="h-[94px] rounded-panel" />)}
        </div>
        <Bar className="h-3 w-64 max-w-full" />
        <div className="grid grid-cols-2 gap-2 pt-4 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Bar key={i} className="h-12 rounded-act" />)}
        </div>
        <div className="flex justify-end"><Bar className="h-10 w-48 rounded-act" /></div>
      </div>
      <div className="flex items-center justify-between border-t border-line-soft px-6 py-5"><Bar className="h-3 w-72 max-w-full" /><Bar className="h-6 w-11 rounded-full" /></div>
    </Panel>
    <Panel>
      <div className="space-y-3 px-6 py-6"><Bar className="h-3 w-16" /><Bar className="h-5 w-40" /><Bar className="h-10 w-full rounded-act" /></div>
    </Panel>
    <Panel>
      <div className="space-y-3 px-6 py-6"><Bar className="h-3 w-16" /><Bar className="h-5 w-48" /></div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3 border-t border-line-soft px-6 py-4">
          <Bar className="h-9 w-9 rounded-tile" />
          <span className="flex-1 space-y-2"><Bar className="h-3 w-32" /><Bar className="h-3 w-56 max-w-full" /></span>
          <Bar className="h-3 w-14" />
        </div>
      ))}
    </Panel>
  </div>
);

interface BillingData {
  account: BillingAccount;
  seats: SeatSummary | null;
  wallet: WalletSummary | null;
  invoices: InvoicePage | null;
  projects: ProjectBudget[];
  history: ActivityEvent[] | null;
}

const loadBillingHistory = () => aaiApi.getActivity('company', BILLING_HISTORY_LIMIT, { category: 'billing' });

export const BillingPanel: React.FC<{ clientId?: string }> = () => {
  const [data, setData] = useState<BillingData | null>(null);
  const [failed, setFailed] = useState(false);
  const [email, setEmail] = useState('');
  const [view, setView] = useState(readBillingView);

  useEffect(clearBillingView, []);
  const [savingEmail, setSavingEmail] = useState(false);

  // Every section loads together and the page appears once: sections that
  // each fetched on their own arrived at different moments and pushed the
  // rest of the page down as they did.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      await finishSeatCheckout();
      await finishCardSetup();
      const [account, seats, wallet, invoices, projects, billingHistory] = await Promise.allSettled([
        billingApi.getAccount(),
        billingApi.getSeats(),
        loadWallet(),
        billingApi.listInvoices(RECENT_INVOICES + 1),
        projectBudgetsApi.list({ kind: 'client' }),
        loadBillingHistory(),
      ]);
      if (cancelled) return;
      if (account.status === 'rejected') {
        setFailed(true);
        return;
      }
      let history: ActivityEvent[] | null = null;
      if (billingHistory.status === 'fulfilled') history = billingHistory.value.data.events;
      setData({
        account: account.value.data,
        seats: seats.status === 'fulfilled' ? seats.value.data : null,
        wallet: wallet.status === 'fulfilled' ? wallet.value : null,
        invoices: invoices.status === 'fulfilled' ? invoices.value.data : null,
        projects: projects.status === 'fulfilled' ? projects.value.data.projects || [] : [],
        history,
      });
      setEmail(account.value.data.billing_email || '');
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (failed) return <ErrorNote message="Could not load billing. Refresh to try again." />;
  if (!data) return <BillingSkeleton />;

  const { account } = data;

  if (!account.billed) {
    const title = 'Your company is not billed';
    const desc = 'Your company is not being charged right now: no seat fee and no usage charges, so your team can invite people and keep working without limits.';
    return (
      <div className="mx-auto max-w-[760px]">
        <Banner mark={<RouteMark icon={<CreditCard size={16} />} size="md" />} kicker="Billing" title={title} desc={desc} />
      </div>
    );
  }

  if (!account.can_manage) {
    return (
      <div className="mx-auto max-w-[760px]">
        <Banner
          mark={<RouteMark icon={<CreditCard size={16} />} size="md" />}
          kicker="Billing"
          title="Billing is managed by your Owners"
          desc="Ask an Owner to change the card or billing email."
        />
      </div>
    );
  }

  const reloadHistory = async () => {
    try {
      const { data: feed } = await loadBillingHistory();
      setData((d) => d && { ...d, history: feed.events });
    } catch {
      /* the list keeps what it showed */
    }
  };

  const reloadAfterSeats = async () => {
    const [wallet, invoices] = await Promise.allSettled([loadWallet(), billingApi.listInvoices(RECENT_INVOICES + 1)]);
    setData((d) => d && {
      ...d,
      wallet: wallet.status === 'fulfilled' && wallet.value ? wallet.value : d.wallet,
      invoices: invoices.status === 'fulfilled' ? invoices.value.data : d.invoices,
    });
    reloadHistory();
  };

  const onSeats = (seats: SeatSummary) => {
    setData((d) => d && { ...d, seats });
    reloadAfterSeats();
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      const { data: saved } = await billingApi.updateBillingEmail(email.trim());
      setData((d) => d && { ...d, account: { ...d.account, billing_email: saved.billing_email, card: saved.card } });
      toast.success('Billing email saved.');
      reloadHistory();
    } catch {
      toast.error('Could not save the billing email. Check the address and try again.');
    } finally {
      setSavingEmail(false);
    }
  };

  const emailDirty = email.trim() !== (account.billing_email || '') && email.trim().length > 3;

  const onTeamPlan = data.seats?.plan === 'workspace';
  const tabs = [{ id: 'billing', label: 'Plan & payments' }];
  if (onTeamPlan) tabs.push({ id: 'renewal', label: 'Renewal' });
  const hasProjects = data.projects.length > 0;
  if (hasProjects) tabs.push({ id: PROJECTS_BILLING_VIEW, label: 'Project budgets' });
  let active = view;
  if (active === 'renewal' && !onTeamPlan) active = 'billing';
  if (active === PROJECTS_BILLING_VIEW && !hasProjects) active = 'billing';
  const pausedProjects = data.projects.filter((p) => p.paused);

  const reloadSeats = async () => {
    try {
      const { data: seats } = await billingApi.getSeats();
      setData((d) => d && { ...d, seats });
      reloadHistory();
    } catch {
      /* the plan card keeps what it showed */
    }
  };

  const viewTabs = (
    <Tabs
      variant="pills"
      label="Billing views"
      active={active}
      onChange={setView}
      tabs={tabs}
      className="!mb-0"
    />
  );

  if (active === 'renewal') {
    return (
      <div className="mx-auto max-w-[1000px] space-y-6">
        {viewTabs}
        <RenewalPanel onChanged={reloadSeats} />
      </div>
    );
  }

  if (active === PROJECTS_BILLING_VIEW) {
    return (
      <div className="mx-auto max-w-[1000px] space-y-6">
        {viewTabs}
        <ProjectBudgetsPanel side={{ kind: 'client' }} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1000px] space-y-6">
      {viewTabs}

      {pausedProjects.length > 0 && (
        <Banner
          mark={<RouteMark icon={<CreditCard size={16} />} size="md" />}
          kicker="Project budgets"
          title={pausedProjects.length === 1 ? `The budget for ${pausedProjects[0].title} is used up` : `${pausedProjects.length} project budgets are used up`}
          desc="Builds on those projects are paused. You can keep going by paying for the project with your own card."
          action={<Action className="min-w-[148px]" onClick={() => setView(PROJECTS_BILLING_VIEW)}>Open project budgets</Action>}
        />
      )}

      <OwnCardPanel
        card={account.card}
        planPayerEmail={account.plan_payer_email}
        viewerEmail={account.viewer_email}
        onTeamPlan={onTeamPlan}
      />

      <PlanSection summary={data.seats} onSummary={onSeats} onRenewal={() => setView('renewal')} />

      <UsageWalletPanel
        wallet={data.wallet}
        onWallet={(wallet) => {
          setData((d) => d && { ...d, wallet });
          reloadHistory();
        }}
        card={account.card}
        viewerEmail={account.viewer_email}
      />

      <Panel>
        <PanelHead kicker="Receipts" title="Billing email" desc="Receipts and invoices go to this address." />
        <div className="px-6 pb-5">
          <Field label="Email" htmlFor="billing-email">
            <div className="flex items-center gap-2">
              <Mail size={16} className="shrink-0 text-ink-dim" />
              <Input id="billing-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <Action onClick={saveEmail} disabled={!emailDirty || savingEmail} reason={savingEmail ? 'Working' : 'Nothing has changed yet.'}>
                Save
              </Action>
            </div>
          </Field>
        </div>
      </Panel>

      <InvoicesPanel page={data.invoices} />

      <BillingHistoryPanel events={data.history} />

      {/* {clientId && <TransferOwnership clientId={clientId} />} */}
    </div>
  );
};

export default BillingPanel;
